import { TeamMemberRole } from '@/types/team';

// Enums matching backend
export enum GameType {
  FFA = 'FFA',
  DUEL = 'DUEL',
  BOTH = 'BOTH',
}

export interface GetGamesParams {
  isActive?: boolean;
}

export interface GameAsset {
  url: string;
  name: string;
  size: number;
  type: string;
}

export interface GameDetails {
  _id: string;
  regions: string[];
  platforms: string[];
  participantsAllowedPerTeam: number[];
  isBettingEnabled: boolean;
  isEntryFeeEnabled: boolean;
  isPrizePoolEnabled: boolean;
  name: string;
  logo: GameAsset;
  gamelogo?: GameAsset;
  bio: string;
  maxPlayers: number;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
  pointsPerKill: number;
  minPlayers: number;
  isActive: boolean;
  pointSystem: Record<string, number>;
  activeBetLogo: GameAsset;
  disabledBetLogo: GameAsset;
  gameType: string;
  participantType: string;
  maxPlayersPerTeam: number;
  scoringFields?: {
    [key: string]: {
      enabled: boolean;
      mandatory: boolean;
      ffa?: boolean;
      duel?: boolean;
      label?: string;
    };
  };
  teamRegistrationFields?: TeamRegistrationField[];
}
export enum RegistrationContext {
  QUICK_REGISTRATION = 'QUICK_REGISTRATION',
  ADD_TO_TEAM = 'ADD_TO_TEAM',
  TEAM_INVITE = 'TEAM_INVITE',
  SOLO_REGISTRATION = 'SOLO_REGISTRATION',
}

export interface RoleContextRule {
  active: boolean;
  mandatory: boolean;
}

export interface TeamRegistrationField {
  fieldLabel: string;
  fieldKey: string;
  inputType: 'NUMBER_ONLY' | 'CHAR_ONLY' | 'ALPHANUMERIC' | 'REGEX' | 'EMAIL';
  minLength?: number;
  maxLength?: number;
  regex?: string; // Only used when inputType is "REGEX"
  errorMessage?: string; // Custom error message for this field
  roleValidationRules: Record<
    TeamMemberRole,
    Record<RegistrationContext, RoleContextRule>
  >;
  generalFormRules?: Record<RegistrationContext, RoleContextRule>;
}

export interface Game {
  _id: string;
  name: string;
  logo: GameAsset;
  gamelogo?: GameAsset;
  bio: string;
  maxPlayers: number;
  minPlayers: number;
  maxPlayersPerTeam: number;
  maxNoOfTeams: number;
  isActive: boolean;
  isDeleted: boolean;
  regions: string[];
  platforms: string[];
  participantsAllowedPerTeam: number[];
  isBettingEnabled: boolean;
  isEntryFeeEnabled: boolean;
  isPrizePoolEnabled: boolean;
  pointSystem: Record<string, number>;
  pointsPerKill: number;
  activeBetLogo?: GameAsset;
  disabledBetLogo?: GameAsset;
  gameType: string;
  participantType: string;
  scoringFields?: {
    [key: string]: {
      enabled: boolean;
      mandatory: boolean;
      ffa?: boolean;
      duel?: boolean;
      label?: string;
    };
  };
  createdAt: string;
  updatedAt: string;
  __v: number;
}

// Game scoring configuration
export interface GameScoringConfig {
  gameType: string;
  individualScoreFields: IndividualScoreField[];
  teamScoreFields: TeamScoreField[];
  supportsMVP: boolean;
  mvpTypes: MVPType[];
  supportsWinnerLoser: boolean; // Flag to show winner/loser selection
  winnerLoserMandatory?: boolean; // Flag to indicate if winner selection is mandatory
  scoringLabel: string; // e.g., "Kills", "Combat Score"
  rankLabel?: string; // Custom label for rank field (e.g., "Rank", "Placement")
  hideRankDropdown?: boolean; // Flag to hide rank dropdown
}

export interface IndividualScoreField {
  key: string;
  label: string;
  type: 'number' | 'boolean';
  required: boolean;
  defaultValue: number | boolean;
  validation?: {
    min?: number;
    max?: number;
  };
}

export interface TeamScoreField {
  key: string;
  label: string;
  type: 'number' | 'boolean';
  required: boolean;
  defaultValue: number | boolean;
  isInputField?: boolean; // Flag to determine if field should be input or display
  validation?: {
    min?: number;
    max?: number;
  };
}

export interface MVPType {
  key: string;
  label: string;
  description: string;
  required: boolean;
  validation?: {
    sameTeamAllowed: boolean;
  };
}

// Dynamic helper to build GameScoringConfig from DB game data
export const buildGameScoringConfig = (
  game: Game | GameDetails,
  competitionType: string,
): GameScoringConfig => {
  const scoringFields = game.scoringFields || {};
  const isFFA = competitionType === GameType.FFA;
  const isDUEL = competitionType === GameType.DUEL;

  // Helper function to check if field should be included
  const shouldIncludeField = (fieldConfig: {
    enabled: boolean;
    ffa?: boolean;
    duel?: boolean;
  }): boolean => {
    if (!fieldConfig.enabled) return false;

    // If ffa/duel flags don't exist, include by default (backward compatibility)
    if (fieldConfig.ffa === undefined && fieldConfig.duel === undefined) {
      return true;
    }

    // Check based on competition type
    if (isFFA) return fieldConfig.ffa ?? false;
    if (isDUEL) return fieldConfig.duel ?? false;

    return false;
  };

  // Build individual score fields dynamically
  const individualScoreFields: IndividualScoreField[] = [];

  // Add combat score if enabled for this competition type
  if (
    scoringFields.combatScore &&
    shouldIncludeField(scoringFields.combatScore)
  ) {
    individualScoreFields.push({
      key: 'score', // Backend expects 'score' for combat score
      label: scoringFields.combatScore.label || 'Combat Score',
      type: 'number',
      required: scoringFields.combatScore.mandatory,
      defaultValue: 0,
      validation: { min: 0 },
    });
  }

  // Add kills if enabled for this competition type
  if (scoringFields.kills && shouldIncludeField(scoringFields.kills)) {
    individualScoreFields.push({
      key: 'kills',
      label: scoringFields.kills.label || 'Kills',
      type: 'number',
      required: scoringFields.kills.mandatory,
      defaultValue: 0,
      validation: { min: 0 },
    });
  }

  // Add deaths if enabled for this competition type
  if (scoringFields.deaths && shouldIncludeField(scoringFields.deaths)) {
    individualScoreFields.push({
      key: 'deaths',
      label: scoringFields.deaths.label || 'Deaths',
      type: 'number',
      required: scoringFields.deaths.mandatory,
      defaultValue: 0,
      validation: { min: 0 },
    });
  }

  // Add assists if enabled for this competition type
  if (scoringFields.assists && shouldIncludeField(scoringFields.assists)) {
    individualScoreFields.push({
      key: 'assists',
      label: scoringFields.assists.label || 'Assists',
      type: 'number',
      required: scoringFields.assists.mandatory,
      defaultValue: 0,
      validation: { min: 0 },
    });
  }

  // Build team score fields - conditionally add team kills only if kills are enabled
  const teamScoreFields: TeamScoreField[] = [];

  // Only add team kills if kills field is enabled
  if (scoringFields.kills && shouldIncludeField(scoringFields.kills)) {
    teamScoreFields.push({
      key: 'kills',
      label: scoringFields.kills.label || 'Team Kills',
      type: 'number',
      required: false,
      defaultValue: 0,
      isInputField: false,
      validation: { min: 0 },
    });
  }

  // Add aggregatedScore to teamScoreFields if enabled
  // This maps to the team-level 'score' field in the DB
  if (
    scoringFields.aggregatedScore &&
    shouldIncludeField(scoringFields.aggregatedScore)
  ) {
    teamScoreFields.push({
      key: 'score', // team-level score in DB (overall/total score)
      label: scoringFields.aggregatedScore.label || 'Aggregated Score',
      type: 'number',
      required: scoringFields.aggregatedScore.mandatory,
      defaultValue: 0,
      isInputField: true,

      validation: { min: 0 },
    });
  }

  // Check if MVP is enabled for this competition type
  const mvpEnabled = scoringFields.mvp && shouldIncludeField(scoringFields.mvp);

  // Build MVP types if enabled
  const mvpTypes: MVPType[] = mvpEnabled
    ? [
        {
          key: 'matchMVP',
          label: 'Match MVP',
          description: 'Most valuable player from the entire match',
          required: scoringFields.mvp.mandatory,
          validation: { sameTeamAllowed: false },
        },
        {
          key: 'teamMVP',
          label: 'Team MVP',
          description:
            'Most valuable player from another team, excluding the Match MVP team',
          required: scoringFields.mvp.mandatory,
          validation: { sameTeamAllowed: false },
        },
      ]
    : [];

  // Check if winner/loser is enabled for this competition type
  const winnerLoserEnabled =
    scoringFields.winnerLoser && shouldIncludeField(scoringFields.winnerLoser);

  // Check if rank is enabled for this competition type
  const rankEnabled =
    scoringFields.rank && shouldIncludeField(scoringFields.rank);
  const hideRankDropdown = !rankEnabled;
  const rankLabel = scoringFields.rank?.label || 'Rank';

  return {
    gameType: game.name,
    individualScoreFields,
    teamScoreFields,
    supportsMVP: !!mvpEnabled,
    mvpTypes,
    supportsWinnerLoser: !!winnerLoserEnabled,
    winnerLoserMandatory: scoringFields.winnerLoser?.mandatory ?? false,
    scoringLabel: scoringFields.combatScore?.enabled
      ? scoringFields.combatScore.label || 'Combat Score'
      : scoringFields.kills?.label || 'Kills',
    rankLabel,
    hideRankDropdown,
  };
};
